/**
 * Marketing report constants.
 *
 * Mirrors backend marketing.types.ts for frontend usage.
 */
import { EQUALITY_FILTER_OPERATORS, type FilterFieldMeta } from '@/lib/filters';

/** Row breakdown options */
export const BREAKDOWN_OPTIONS = [
  { value: 'campaign_name', label: 'Campaign Name' },
  { value: 'campaign_halo', label: 'Campaign Halo' },
  { value: 'creative_name', label: 'Creative Name' },
  { value: 'creative_halo', label: 'Creative Halo' },
  { value: 'ad_group_name', label: 'Ad Group Name' },
  { value: 'ad_group_halo', label: 'Ad Group Halo' },
  { value: 'country', label: 'Country' },
  { value: 'platform', label: 'Platform' },
  { value: 'app_version', label: 'App Version' },
  { value: 'tier_name', label: 'Tier' },
  { value: 'traffic_source', label: 'Traffic Source' },
] as const;

/** All metrics (unified list — KPIs compute based on available data) */
export const ALL_METRICS = [
  { value: 'cost', label: 'Cost (₺)' },
  { value: 'revenue', label: 'Revenue (₺)' },
  { value: 'refunds', label: 'Refunds (₺)' },
  { value: 'refund_rate', label: 'Refund Rate (%)' },
  { value: 'impressions', label: 'Ad Impressions' },
  { value: 'clicks', label: 'Ad Clicks' },
  { value: 'installs', label: 'Installs' },
  { value: 'cpi', label: 'CPI' },
  { value: 'roas', label: 'ROAS' },
  { value: 'ltv', label: 'LTV' },
  { value: 'cvr', label: 'CVR (%)' },
  { value: 'pcvr', label: 'PCVR (%)' },
  { value: 'cpp', label: 'CPP' },
  { value: 'total_purchases', label: 'Total Purchases' },
  { value: 'initial_purchases', label: 'Initial Purchases' },
  { value: 'renewals', label: 'Renewals' },
  { value: 'special_offer_purchases', label: 'Special Offer Purchases' },
  { value: 'landing_page_visits', label: 'Landing Page Visits' },
  { value: 'landing_page_clicks', label: 'Landing Page Clicks' },
] as const;

/** Default selected metrics */
export const DEFAULT_METRICS = [
  'cost', 'revenue', 'installs', 'cpi', 'roas',
];

/** Marketing filter fields */
export const MARKETING_FILTER_FIELDS = [
  { value: 'campaign_name', label: 'campaign_name' },
  { value: 'creative_name', label: 'creative_name' },
  { value: 'ad_group_name', label: 'ad_group_name' },
  { value: 'country', label: 'country' },
  { value: 'platform', label: 'platform' },
  { value: 'app_version', label: 'app_version' },
  { value: 'tier_name', label: 'tier_name' },
  { value: 'traffic_source', label: 'traffic_source' },
] as const;

export const MARKETING_FILTER_OPERATORS = EQUALITY_FILTER_OPERATORS;

export const MARKETING_FILTER_FIELD_META: FilterFieldMeta = {
  campaign_name:  { type: 'text', placeholder: 'e.g., PL Tier 1' },
  creative_name:  { type: 'text', placeholder: 'e.g., Tier1_Hook_v3' },
  ad_group_name:  { type: 'text', placeholder: 'e.g., Profile Viewers' },
  country:        { type: 'text', placeholder: 'e.g., US' },
  platform: {
    type: 'dropdown',
    options: [
      { value: 'Android', label: 'Android' },
      { value: 'iOS', label: 'iOS' },
    ],
  },
  app_version:    { type: 'text', placeholder: 'e.g., 2.9.3' },
  tier_name:      { type: 'text', placeholder: 'e.g., Tier 1' },
  traffic_source: {
    type: 'dropdown',
    options: [
      { value: 'google_ads', label: 'Google Ads' },
      { value: 'apple_search_ads', label: 'Apple Search Ads' },
      { value: 'meta_ads', label: 'Meta Ads' },
      { value: 'tiktok_ads', label: 'TikTok Ads' },
      { value: 'organic', label: 'Organic' },
    ],
  },
};

/**
 * Metrics that are only meaningful when breakdown is campaign_name.
 */
export const CAMPAIGN_ONLY_METRICS = new Set([
  'impressions',
  'clicks',
  'cvr',
  'landing_page_visits',
  'landing_page_clicks',
]);

/** Helper to get metrics list, optionally filtering by breakdown */
export function getAvailableMetrics(breakdown?: string) {
  if (
    breakdown &&
    breakdown !== 'campaign_name' &&
    breakdown !== 'campaign_halo' &&
    breakdown !== 'creative_name' &&
    breakdown !== 'creative_halo' &&
    breakdown !== 'ad_group_name' &&
    breakdown !== 'ad_group_halo'
  ) {
    return ALL_METRICS.filter((m) => !CAMPAIGN_ONLY_METRICS.has(m.value));
  }
  return ALL_METRICS;
}
